import { type ReactNode } from 'react';
import { formatIst } from '../../lib/time';
import { humanLabel } from './activityConstants';

// Shared human-readable renderer for log values (used by the Activity + WhatsApp
// drawers). NEVER dumps raw JSON — objects become key/value cards, arrays become
// chips/cards, booleans → Yes/No, enum-shaped strings are softened, ISO dates
// are formatted, numeric strings drop trailing zeros.

function softenEnum(s: unknown): string {
  if (typeof s !== 'string') return String(s);
  if (!/^[A-Z][A-Z0-9_]{2,}$/.test(s)) return s;
  return s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim();
  if (/^-?\d+\.\d+$/.test(s)) { const n = Number(s); if (Number.isFinite(n)) return n.toString(); }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)) return formatIst(s);
  return s;
}

export function ObjectCard({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!entries.length) return <span className="text-muted italic">—</span>;
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-xs leading-tight">
          <span className="min-w-[90px] shrink-0 text-muted">{humanLabel(k)}</span>
          <span className="break-words text-content">
            {v && typeof v === 'object' ? <LogValue value={v} /> : softenEnum(formatScalar(v))}
          </span>
        </div>
      ))}
    </div>
  );
}

export function LogValue({ value }: { value: unknown }): ReactNode {
  if (value === undefined || value === null || value === '') return <span className="text-muted italic">—</span>;
  if (typeof value === 'boolean') return value ? <span className="font-medium text-emerald-600">Yes</span> : <span className="font-medium text-rose-600">No</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-muted italic">empty</span>;
    if (value.every((v) => v && typeof v === 'object')) {
      return <div className="space-y-1.5">{value.map((it, i) => <div key={i} className="rounded-control border border-line bg-surface px-2 py-1.5"><ObjectCard data={it as Record<string, unknown>} /></div>)}</div>;
    }
    return <div className="flex flex-wrap gap-1">{value.map((v, i) => <span key={i} className="inline-block rounded bg-surface-2 px-1.5 py-0.5 text-2xs text-content">{softenEnum(formatScalar(v))}</span>)}</div>;
  }
  if (typeof value === 'object') return <ObjectCard data={value as Record<string, unknown>} />;
  return <span className="break-words text-content">{formatScalar(value)}</span>;
}

/** Parse a stored JSON string into an object/array for LogValue; pass scalars through. */
export function parseMaybeJson(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const s = v.trim();
  if (!(s.startsWith('{') || s.startsWith('['))) return v;
  try { return JSON.parse(s); } catch { return v; }
}
